/**
 * RC Product - JavaScript functionality
 * Handles variant selection, gallery, quantity, and cart interactions
 */

import { CartAddEvent, CartErrorEvent } from '@theme/rc-events';

/* ==========================================================================
   Product Gallery (Mobile Carousel)
   ========================================================================== */

class RCProductGallery extends HTMLElement {
  constructor() {
    super();
    this.carousel = null;
    this.dots = [];
    this.currentIndex = 0;
  }

  connectedCallback() {
    this.carousel = this.querySelector('.rc-product__carousel');
    this.dots = Array.from(this.querySelectorAll('.rc-product__dot'));

    if (this.carousel) {
      this.carousel.addEventListener('scroll', this.handleScroll.bind(this));
    }

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });
  }

  handleScroll() {
    const scrollLeft = this.carousel.scrollLeft;
    const itemWidth = this.carousel.querySelector('.rc-product__carousel-item')?.offsetWidth || 0;

    if (itemWidth > 0) {
      const newIndex = Math.round(scrollLeft / itemWidth);
      if (newIndex !== this.currentIndex) {
        this.currentIndex = newIndex;
        this.updateDots();
      }
    }
  }

  goToSlide(index) {
    const item = this.carousel.querySelector(`.rc-product__carousel-item[data-index="${index}"]`);
    if (item) {
      item.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      this.currentIndex = index;
      this.updateDots();
    }
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.currentIndex);
    });
  }
}

if (!customElements.get('rc-product-gallery')) {
  customElements.define('rc-product-gallery', RCProductGallery);
}

/* ==========================================================================
   Product Slider (Desktop Horizontal Scroll)
   ========================================================================== */

class RCProductSlider extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.isDragging = false;
    this.startX = 0;
    this.scrollLeft = 0;
  }

  connectedCallback() {
    this.track = this.querySelector('.rc-product__slider-track');

    if (this.track) {
      this.track.addEventListener('mousedown', this.handleMouseDown.bind(this));
      this.track.addEventListener('mousemove', this.handleMouseMove.bind(this));
      this.track.addEventListener('mouseup', this.handleMouseUp.bind(this));
      this.track.addEventListener('mouseleave', this.handleMouseUp.bind(this));

      // Touch support
      this.track.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
      this.track.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: true });
      this.track.addEventListener('touchend', this.handleTouchEnd.bind(this));
    }
  }

  handleMouseDown(e) {
    this.isDragging = true;
    this.startX = e.pageX - this.track.offsetLeft;
    this.scrollLeft = this.track.scrollLeft;
    this.track.style.cursor = 'grabbing';
  }

  handleMouseMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();
    const x = e.pageX - this.track.offsetLeft;
    const walk = (x - this.startX) * 1.5;
    this.track.scrollLeft = this.scrollLeft - walk;
  }

  handleMouseUp() {
    this.isDragging = false;
    this.track.style.cursor = 'grab';
  }

  handleTouchStart(e) {
    this.startX = e.touches[0].pageX - this.track.offsetLeft;
    this.scrollLeft = this.track.scrollLeft;
  }

  handleTouchMove(e) {
    const x = e.touches[0].pageX - this.track.offsetLeft;
    const walk = (x - this.startX) * 1.5;
    this.track.scrollLeft = this.scrollLeft - walk;
  }

  handleTouchEnd() {
    // Optional: snap to nearest item
  }
}

if (!customElements.get('rc-product-slider')) {
  customElements.define('rc-product-slider', RCProductSlider);
}

/* ==========================================================================
   Variant Selection Handler
   ========================================================================== */

class RCProductVariantHandler {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.section = document.querySelector(`#rc-product-${sectionId}`);
    this.form = document.getElementById('rc-product-form');
    this.productData = this.getProductData();

    if (!this.section || !this.productData) return;

    this.init();
  }

  getProductData() {
    const jsonEl = document.getElementById(`rc-product-json-${this.sectionId}`);
    if (!jsonEl) return null;
    try {
      return JSON.parse(jsonEl.textContent);
    } catch (e) {
      console.error('Failed to parse product JSON:', e);
      return null;
    }
  }

  init() {
    // Listen for option changes
    this.section.querySelectorAll('input[name^="option-"]').forEach(input => {
      input.addEventListener('change', () => this.updateVariant());
    });

    // Listen for swatch clicks
    this.section.querySelectorAll('.rc-product__swatch').forEach(swatch => {
      swatch.addEventListener('click', (e) => this.handleSwatchClick(e));
    });

    // Listen for quantity buttons
    this.section.querySelectorAll('.rc-product__quantity-btn').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleQuantity(e));
    });

    // Handle form submission with AJAX
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleAddToCart(e));
    }
  }

  async handleAddToCart(e) {
    e.preventDefault();

    const addBtn = this.section.querySelector('.rc-product__add-to-cart');
    if (!addBtn || addBtn.disabled) return;

    const formData = new FormData(this.form);
    const originalText = addBtn.textContent;

    // Show loading state
    addBtn.disabled = true;
    addBtn.textContent = window.Theme?.translations?.adding || 'Adding...';

    try {
      // Get the sections to update (for cart drawer)
      const sectionsToUpdate = this.getSectionsToRender();

      // Add sections parameter to formData
      formData.append('sections', sectionsToUpdate.join(','));
      formData.append('sections_url', window.location.pathname);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      const data = await response.json();

      if (response.ok) {
        // Success - show added state
        addBtn.textContent = window.Theme?.translations?.added || 'Added!';

        // Dispatch CartAddEvent to trigger cart drawer (using theme's event system)
        document.dispatchEvent(
          new CartAddEvent(data, this.form.id, {
            source: 'rc-product-form',
            itemCount: parseInt(formData.get('quantity')) || 1,
            productId: this.productData.id,
            variantId: formData.get('id'),
            sections: data.sections || {}
          })
        );

        // Reset button after delay
        setTimeout(() => {
          addBtn.disabled = false;
          addBtn.textContent = originalText;
        }, 2000);
      } else {
        // Error from Shopify
        const errorData = data;
        document.dispatchEvent(
          new CartErrorEvent(this.form.id, errorData.message, errorData.description, errorData.errors)
        );
        throw new Error(errorData.message || errorData.description || 'Could not add to cart');
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      addBtn.textContent = error.message || 'Error';

      setTimeout(() => {
        addBtn.disabled = false;
        addBtn.textContent = originalText;
      }, 2000);
    }
  }

  getSectionsToRender() {
    // Get section IDs that need to be re-rendered after cart update
    const sections = [];

    // Cart drawer section
    const cartDrawer = document.querySelector('rc-cart-drawer-component');
    if (cartDrawer) {
      const cartItemsComponent = cartDrawer.querySelector('rc-cart-items-component');
      if (cartItemsComponent) {
        const sectionId = cartItemsComponent.dataset.sectionId;
        if (sectionId) {
          sections.push(sectionId);
        }
      }
    }

    // Header cart count
    const header = document.querySelector('[data-section-type="header"]');
    if (header) {
      sections.push(header.dataset.sectionId || 'header');
    }

    return sections;
  }

  updateVariant() {
    const selectedOptions = [];
    this.section.querySelectorAll('input[name^="option-"]:checked').forEach(input => {
      const position = parseInt(input.dataset.optionPosition);
      selectedOptions[position - 1] = input.value;
    });

    const variant = this.productData.variants.find(v => {
      return v.options.every((opt, i) => opt === selectedOptions[i]);
    });

    if (variant) {
      this.updateUI(variant);
    }
  }

  updateUI(variant) {
    // Update hidden variant ID input
    const variantInput = document.getElementById('rc-product-variant-id');
    if (variantInput) {
      variantInput.value = variant.id;
    }

    // Update price
    const priceEl = this.section.querySelector('[data-product-price]');
    if (priceEl) {
      priceEl.textContent = this.formatMoney(variant.price);
    }

    // Update SKU
    const skuEl = this.section.querySelector('[data-product-sku]');
    if (skuEl && variant.sku) {
      skuEl.textContent = variant.sku;
    }

    // Update add to cart button
    const addBtn = this.section.querySelector('.rc-product__add-to-cart');
    if (addBtn) {
      if (variant.available) {
        addBtn.disabled = false;
        addBtn.textContent = addBtn.dataset.addText || 'Add to cart';
      } else {
        addBtn.disabled = true;
        addBtn.textContent = addBtn.dataset.soldOutText || 'Sold out';
      }
    }

    // Update URL
    this.updateURL(variant);
  }

  updateURL(variant) {
    if (!variant) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('variant');
    window.history.replaceState({}, '', url.toString());
  }

  handleSwatchClick(e) {
    const swatch = e.currentTarget;
    const optionValue = swatch.dataset.optionValue;

    // Find and click the corresponding radio input
    const radio = this.section.querySelector(`input[value="${optionValue}"]`);
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change'));
    }

    // Update swatch selection UI
    this.section.querySelectorAll('.rc-product__swatch').forEach(s => {
      s.classList.remove('is-selected');
    });
    swatch.classList.add('is-selected');
  }

  handleQuantity(e) {
    const btn = e.currentTarget;
    const action = btn.dataset.action;
    const input = this.section.querySelector('.rc-product__quantity-input');

    if (!input) return;

    let value = parseInt(input.value) || 1;

    if (action === 'increase') {
      value++;
    } else if (action === 'decrease' && value > 1) {
      value--;
    }

    input.value = value;
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    // Basic formatting - could be enhanced with Shopify's money format
    return `${amount}€`;
  }
}

/* ==========================================================================
   Initialize on DOM Ready
   ========================================================================== */

function initRCProduct() {
  // Find all RC Product sections and initialize handlers
  document.querySelectorAll('.rc-product').forEach((section) => {
    const sectionId = section.dataset.sectionId;
    if (sectionId && !section.dataset.initialized) {
      section.dataset.initialized = 'true';
      new RCProductVariantHandler(sectionId);
    }
  });
}

// Initialize immediately if DOM is ready, otherwise wait
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRCProduct);
} else {
  initRCProduct();
}

/* ==========================================================================
   Shopify Section Render Events (Theme Editor)
   ========================================================================== */

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('.rc-product');
  if (section) {
    const sectionId = section.dataset.sectionId;
    if (sectionId) {
      new RCProductVariantHandler(sectionId);
    }
  }
});
